// FAQ della verifica Takata — mostrate nel registro (quarto pulsante di
// IstruzioniEstensione) e base della comunicazione agli associati. Due gruppi:
//  - CASI_TAKATA: i casi operativi dello strumento, emersi dal campo;
//  - FAQ_SEGRETERIA: le FAQ ufficiali della Segreteria Nazionale sul Decreto 229/2026
//    (testo istituzionale, fornito dalla Segreteria: non riformulare nella sostanza).
//
// COME SI AGGIORNA: skill `aggiorna-casi-takata` (.claude/skills/). Ogni caso nuovo
// emerso dal campo va aggiunto QUI, in linguaggio semplice (stile changelog associati:
// niente nomi di file, niente gergo). I casi più frequenti stanno in cima.
// Ogni voce nasce da un caso reale: non inventare casi ipotetici.

export interface CasoTakata {
  id: string;
  titolo: string;
  corpo: string[];
}

export const CASI_TAKATA: CasoTakata[] = [
  {
    id: 'verifica-multipla',
    titolo: 'Devo verificare molti telai di pratiche già lavorate',
    corpo: [
      'Usa «Verifica multipla» in cima al registro: incolli fino a 50 telai alla volta (da Excel, dal gestionale o da un documento) e il sistema li divide fra quelli che si verificano da soli e quelli che chiedono un codice di sicurezza o una spunta.',
      'Con «Avvia verifica senza intervento» i primi si verificano senza che tu faccia nulla; con «Avvia verifica con intervento» si apre un portale alla volta, completi il codice e il resto lo fa l\'estensione. Durante il giro non si scaricano certificati.',
      'Alla fine scarichi l\'elenco degli esiti per Excel e, dove serve (di solito solo per i coinvolti), il certificato della singola riga. Ogni verifica è anche nel registro, con data, ora e schermata del portale.',
      'Se un portale non dà l\'esito (per esempio risponde «modello non trovato»), dopo un minuto e mezzo il telaio viene segnato «Da rifare» per un problema tecnico e il giro passa da solo al successivo. Quel telaio lo verifichi poi singolarmente.',
      'Durante il giro lascia in primo piano la finestra dei portali che si apre. Serve l\'estensione 1.2.7 o successiva: Chrome la aggiorna da solo, e se la pagina ti chiede di aggiornarla basta chiudere e riaprire Chrome e ricaricare l\'area riservata.',
    ],
  },
  {
    id: 'marca-non-riconosciuta',
    titolo: 'Il sistema non riconosce la marca dal telaio',
    corpo: [
      'Il codice del telaio è un suggerimento, non una certezza: alcune vetture (specie quelle mai vendute negli Stati Uniti) non si riconoscono.',
      'Scegli la marca dalla tendina del pannello e prosegui normalmente: vale la tua scelta.',
    ],
  },
  {
    id: 'marca-fuori-elenco',
    titolo: 'L\'esito dice «Marca non compresa nell\'elenco del Ministero»',
    corpo: [
      'Il telaio è di una marca che non rientra nella procedura del Ministero: per esempio Fiat, Alfa Romeo, Abarth, Maserati, Renault, Dacia, Kia, Hyundai, Volvo, Smart o Porsche. La verifica resta nel registro, ma il certificato non serve e non viene prodotto. Mini invece è compresa e si verifica sul portale BMW.',
      'La marca la deduce il sistema dal telaio. Se la carta di circolazione riporta un\'altra marca (succede con alcuni modelli costruiti da una casa per un\'altra), correggi la marca e ripeti la verifica. Nella verifica multipla la correggi dalla riga del telaio e poi riavvii la verifica.',
    ],
  },
  {
    id: 'cookie-portali',
    titolo: 'Alla prima apertura di un portale compaiono i cookie',
    corpo: [
      'Accettali (o rifiutali, come preferisci) prima che la verifica si concluda: altrimenti il banner finisce nella schermata di prova allegata al certificato, coprendo l\'esito.',
      'Succede solo la prima volta per ogni portale: la scelta viene ricordata.',
    ],
  },
  {
    id: 'impossibile-verificare',
    titolo: 'Il certificato dice «IMPOSSIBILE VERIFICARE»',
    corpo: [
      'Non è un errore: il portale della casa ha risposto di non avere dati per quel telaio. Capita con i veicoli di recente immatricolazione (es. Mercedes «Purtroppo non disponiamo di informazioni») o quando il portale non trova il veicolo (es. Land Rover «Rivolgersi al proprio concessionario»).',
      'Il certificato, con la schermata allegata, documenta che la verifica è stata fatta e qual è stata la risposta della fonte: conservalo come gli altri.',
    ],
  },
  {
    id: 'gia-riparato',
    titolo: 'Il portale dice che il veicolo è già stato riparato',
    corpo: [
      'Alcuni portali (es. Volkswagen, Audi, Seat, DAF) rispondono che il veicolo era coinvolto ma l\'intervento è già stato eseguito. Seat lo scrive così: «è già stato riparato nell\'ambito di una campagna di richiamo… non è richiesta alcuna ulteriore azione»; questa risposta la riconosce l\'estensione dalla versione 1.2.6 in poi: se la verifica resta ferma senza esito, chiudi e riapri Chrome per ricevere l\'aggiornamento. Lo stesso fa l\'archivio Stellantis (Jeep, Chrysler, Dodge, Lancia Thema): elenca la campagna con lo stato «REPAIRED» e la data della riparazione.',
      'Il certificato esce automaticamente come NON COINVOLTO, con la schermata (o la risposta) del portale come prova della riparazione. Fino al 5 settembre 2026 i veicoli Stellantis già riparati uscivano per errore come COINVOLTI: i certificati interessati sono stati corretti e basta riscaricarli dal registro.',
    ],
  },
  {
    id: 'altri-richiami',
    titolo: 'Il portale segnala un richiamo che non riguarda gli airbag',
    corpo: [
      'Gli strumenti dei costruttori coprono tutti i richiami, non solo il Takata: può comparire un «richiamo», un «aggiornamento tecnico» (BMW), una «campagna di richiamo/aggiornamento» (Honda) o un richiamo su un altro componente (es. BMW «richiamo del motorino di avviamento») che con gli airbag non c\'entra.',
      'Il certificato esce come NON COINVOLTO nel richiamo Takata, con la segnalazione degli altri richiami e — dove la fonte lo dice — la precisazione che risultano già effettuati.',
    ],
  },
  {
    id: 'unrae-nuovo-portale',
    titolo: 'Nissan, Mitsubishi, Subaru e Daimler Truck: la verifica passa dal portale UNRAE',
    corpo: [
      'Dal 28 settembre 2026 UNRAE ha rifatto il proprio portale e la verifica automatica «senza aprire nulla» che avevamo per queste marche non è più possibile: ora si apre la pagina UNRAE come per gli altri portali guidati. L\'estensione sceglie la marca, compila il telaio e avvia la verifica da sola; tu devi solo aspettare l\'esito (se comparisse la casella «Non sono un robot», spuntala).',
      'Daihatsu non viene più verificata da UNRAE: l\'estensione apre il sito del distributore italiano (dps-italia.it), dove inserisci il telaio e l\'esito viene catturato come sugli altri portali.',
    ],
  },
  {
    id: 'camion-mercedes',
    titolo: 'Camion e furgoni Mercedes (telai W1T/WDB/WDF)',
    corpo: [
      'I camion Mercedes (Actros, Arocs e simili) si verificano come «Daimler Truck», non come «Mercedes-Benz»: il sistema ora li riconosce da solo dal telaio e la verifica parte automaticamente, senza aprire alcun portale.',
      'Se il riconoscimento non scattasse, seleziona «Daimler Truck» dalla tendina: vale sempre la tua scelta. Il portale Mercedes-Benz resta quello giusto per vetture e furgoni (es. Sprinter, Vito).',
    ],
  },
  {
    id: 'camion-daf',
    titolo: 'Camion DAF: il portale vuole solo 8 caratteri',
    corpo: [
      'La pagina di controllo DAF non chiede il telaio intero ma solo gli ultimi 8 caratteri (quelli stampati sullo sportello). Tu inserisci comunque il telaio completo nel pannello: ci pensa l\'estensione ad accorciarlo e a premere «Controlla».',
      'Se il portale risponde «Gli aggiornamenti sono già stati eseguiti per il suo veicolo», il certificato esce come NON COINVOLTO, con la schermata come prova.',
    ],
  },
  {
    id: 'esito-nuovo',
    titolo: 'Il portale risponde ma la verifica non si conclude',
    corpo: [
      'Alcuni portali mostrano ogni tanto esiti scritti in un modo mai visto prima (è successo ad esempio con la campagna Opel «KBM - Modulo Air bag», con il richiamo BMW del motorino di avviamento e con il primo veicolo Škoda coinvolto): in quei casi la verifica resta in attesa perché lo strumento non riconosce la frase.',
      'Segnalacelo col telaio dal pulsante «Segnala un problema» del registro: i casi vengono sistemati in poche ore e l\'estensione si aggiorna da sola, senza che tu debba fare nulla.',
    ],
  },
  {
    id: 'sessione-scaduta',
    titolo: 'Il pannello dice «Sessione scaduta» o «Non collegato»',
    corpo: [
      'Il collegamento con l\'area riservata dura alcuni giorni, poi va rinnovato: premi «Accedi» nel pannello, entra nell\'area riservata e riprova.',
      'Se sei già dentro l\'area riservata ma il pannello insiste con «Non collegato», la tua sessione sul sito non è più valida (può succedere dopo un intervento di manutenzione sui nostri server): esci con «Logout» e rientra. Da settembre 2026 il sito se ne accorge da solo all\'ingresso nell\'area riservata e ti riporta alla pagina di accesso.',
    ],
  },
  {
    id: 'dati-acquirente',
    titolo: 'Veicolo COINVOLTO: i dati dell\'acquirente',
    corpo: [
      'Per i veicoli coinvolti il certificato include la dichiarazione di presa visione da far firmare all\'acquirente.',
      'Nel registro premi la matita sulla riga della verifica, inserisci i dati (o la società, se firma il legale rappresentante) e riscarica il documento.',
    ],
  },
];

// FAQ ufficiali della Segreteria Nazionale sul Decreto 229/2026 (agosto 2026).
// Testo istituzionale fornito dalla Segreteria: eventuali aggiornamenti arrivano da lì.
export const FAQ_SEGRETERIA: CasoTakata[] = [
  {
    id: 'sns-richiamo-non-takata',
    titolo: 'Il portale del costruttore restituisce un richiamo che non riguarda gli airbag Takata. Come dobbiamo comportarci?',
    corpo: [
      'Il Decreto 229/2026 riguarda esclusivamente le campagne di richiamo relative agli airbag Takata.',
      'Può accadere che alcuni portali delle case costruttrici restituiscano tutte le campagne di richiamo aperte sul veicolo e non esclusivamente quelle relative agli airbag Takata. In tali casi è necessario individuare solo quello che riguardi effettivamente gli airbag Takata.',
      'Qualora il richiamo sia riferito ad altri interventi tecnici (ad esempio aggiornamenti software o altre campagne di servizio), non trovano applicazione gli adempimenti previsti dall\'art. 6 del Decreto 229 e non deve essere acquisita la dichiarazione prevista per i richiami Takata.',
    ],
  },
  {
    id: 'sns-quando-dichiarazione',
    titolo: 'Quando deve essere acquisita la dichiarazione prevista dall\'art. 6 del Decreto 229?',
    corpo: [
      'La dichiarazione deve essere acquisita esclusivamente in presenza di una campagna di richiamo Takata pendente.',
      'Se il richiamo è già stato eseguito e il sistema ufficiale del costruttore ne attesta la chiusura, la dichiarazione non è necessaria.',
    ],
  },
  {
    id: 'sns-marchi-non-interessati',
    titolo: 'Come comportarsi con i marchi che non risultano interessati dalla procedura ministeriale?',
    corpo: [
      'La verifica deve essere effettuata esclusivamente attraverso i canali ufficiali indicati dal Ministero delle Infrastrutture e dei Trasporti.',
      'Per i marchi che non risultano attualmente ricompresi nella procedura ministeriale (ad esempio Fiat e Smart) non è richiesta alcuna ulteriore verifica ai fini dell\'applicazione del Decreto n. 229/2026.',
    ],
  },
  {
    id: 'sns-rinvio-concessionario',
    titolo: 'Alcuni portali non restituiscono direttamente l\'esito della verifica ma rinviano al concessionario. È corretto?',
    corpo: [
      'Sì. Qualora il costruttore abbia previsto che la verifica venga effettuata tramite la propria rete autorizzata, tale modalità costituisce il canale ufficiale previsto dall\'art. 3, comma 2, lett. d), del Decreto 229.',
    ],
  },
  {
    id: 'sns-anni-2001-2019',
    titolo: 'Il riferimento temporale 2001-2019 è vincolante?',
    corpo: [
      'No. L\'art. 8 del Decreto stabilisce espressamente che le disposizioni si applicano a tutti i veicoli interessati da campagne di richiamo Takata, indipendentemente dalla data di immatricolazione.',
      'Il riferimento agli anni 2001-2019 costituisce pertanto un\'indicazione concernente la maggior parte dei veicoli coinvolti, ma non sostituisce l\'esito della verifica del numero di telaio.',
    ],
  },
  {
    id: 'sns-certificato-sostituisce',
    titolo: 'Il Certificato di Verifica Takata introdotto da UNASCA (comunicato prot. n. 372/SNS/lb del 30.07.2026) sostituisce i controlli previsti dal Decreto?',
    corpo: [
      'Il Certificato di Verifica Takata, messo gratuitamente a disposizione da UNASCA, costituisce uno strumento di supporto operativo che consente di documentare l\'avvenuta verifica, la data e l\'ora del controllo e l\'esito restituito dai canali ufficiali.',
      'Il certificato rappresenta un\'importante tutela documentale per lo Studio e può essere conservato agli atti della pratica. In questa prima fase è in versione BETA quindi è importante prestare attenzione.',
    ],
  },
  {
    id: 'sns-strumento-beta',
    titolo: 'Lo strumento UNASCA è definitivo?',
    corpo: [
      'Lo strumento è stato rilasciato in versione BETA per consentire agli Associati di disporre immediatamente di un supporto operativo.',
      'Le numerose segnalazioni ricevute in questi giorni sono estremamente utili e consentiranno di migliorare progressivamente il servizio e di confrontarci con i soggetti interessati qualora emergano criticità nei sistemi di interrogazione messi a disposizione dai costruttori.',
      'Invitiamo pertanto tutti gli Associati a continuare a segnalare eventuali anomalie utilizzando l\'apposita funzione presente nell\'applicativo.',
    ],
  },
  {
    id: 'sns-richiamo-pendente-formalita',
    titolo: 'La pendenza di una campagna di richiamo Takata impedisce la chiusura della formalità?',
    corpo: [
      'No. Il richiamo pendente non impedisce il perfezionamento della formalità. In tale ipotesi occorre acquisire la dichiarazione prevista dall\'art. 6 del Decreto 229 e conservarla agli atti della pratica, unitamente all\'evidenza della verifica effettuata sui canali ufficiali. La formalità può quindi essere regolarmente registrata.',
      'Invitiamo a prestare particolare attenzione a questo concetto, che è all\'origine di gran parte dei quesiti pervenuti: la verifica e la dichiarazione sono adempimenti documentali, non condizioni ostative alla pratica.',
    ],
  },
  {
    id: 'sns-fermi-registrazione',
    titolo: 'Qual è oggi la situazione dei fermi in registrazione?',
    corpo: [
      'Nella giornata del 3 agosto si sono verificati fermi in fase di registrazione dei passaggi di proprietà relativi a veicoli interessati da campagne di richiamo Takata con provvedimento di «stop drive»: in tali casi la pratica non risultava lavorabile.',
      'Dal 4 agosto il blocco è stato rimosso e il sistema restituisce un ostativo di tipo non bloccante, che segnala la presenza del richiamo senza impedire il perfezionamento della pratica. Le pratiche rimaste ferme nella giornata del 3 agosto possono pertanto essere ripresentate.',
    ],
  },
  {
    id: 'sns-esiti-discordanti',
    titolo: 'Lo strumento UNASCA e il portale del costruttore restituiscono esiti discordanti. Come ci si deve comportare?',
    corpo: [
      'Il Certificato di Verifica Takata non costituisce una banca dati autonoma: interroga i canali ufficiali messi a disposizione dai costruttori e ne fotografa l\'esito. Le discrepanze segnalate derivano quindi dai sistemi di interrogazione a monte, quali, per citare, tempi di allineamento degli archivi, risposte differenziate tra portale pubblico e rete autorizzata, diversa gestione delle campagne già chiuse.',
      'In caso di esiti discordanti fa fede il riscontro del canale ufficiale del costruttore, che va conservato agli atti della pratica insieme all\'evidenza dell\'altra verifica effettuata.',
      'Un chiarimento in punto di responsabilità, oggetto di comprensibile preoccupazione: lo Studio che effettua e documenta la verifica attraverso i canali ufficiali previsti dall\'art. 3 del Decreto ha adempiuto a quanto richiesto. L\'allineamento delle banche dati dei costruttori non rientra tra gli obblighi posti a carico degli Studi.',
    ],
  },
  {
    id: 'sns-rete-senza-riscontro',
    titolo: 'Alcuni costruttori rinviano alla rete autorizzata, che però non fornisce alcun riscontro sul telaio o pretende la visione fisica del veicolo. Esiste una deroga per gli Studi?',
    corpo: [
      'Allo stato non esiste alcuna deroga che consenta agli Studi di ottenere direttamente dal costruttore l\'esito della verifica sul numero di telaio. Quando il costruttore ha previsto che la verifica avvenga tramite la propria rete autorizzata, tale modalità costituisce il canale ufficiale ai sensi dell\'art. 3, comma 2, lett. d), del Decreto 229 (come già chiarito al punto 4 della prot. n. 380/SNS/ar del 3/8). La scelta delle modalità di erogazione del riscontro resta in capo al costruttore.',
      'In queste situazioni raccomandiamo di documentare puntualmente il tentativo di verifica — data, ora, officina o numero verde contattato, riscontro ricevuto o mancato riscontro — e di conservare tale evidenza agli atti insieme al Certificato di Verifica Takata.',
      'Le segnalazioni su questo punto, in particolare quelle relative a reti autorizzate che non forniscono alcun dato o che subordinano il riscontro alla visione fisica del veicolo, sono in corso di formalizzazione verso il Ministero e i costruttori interessati: tale modalità rende infatti di fatto inapplicabile la procedura in sede di pratica.',
    ],
  },
  {
    id: 'sns-veicoli-importazione',
    titolo: 'Come ci si comporta con i veicoli di importazione?',
    corpo: [
      'Vale il medesimo criterio previsto per i veicoli nazionali: interrogazione del numero di telaio sui canali ufficiali del costruttore, con conservazione dell\'esito agli atti. L\'Associazione non dispone di banche dati alternative, né per il mercato nazionale né per i veicoli di importazione.',
      'Come già precisato al punto 5 della prot. n. 380/SNS/ar del 3/8, l\'art. 8 del Decreto 229 si applica a tutti i veicoli interessati da campagne di richiamo Takata indipendentemente dalla data di immatricolazione: è l\'esito della verifica sul telaio a rilevare, non l\'annualità.',
    ],
  },
  {
    id: 'sns-esportazioni',
    titolo: 'Nelle esportazioni, dove l\'eventuale compratore del veicolo non è presente, che cosa deve essere acquisito?',
    corpo: [
      'Nulla. Le formalità di cessazione della circolazione per esportazione non rientrano nell\'ambito applicativo del Decreto Direttoriale 229/2026. Non trovano pertanto applicazione né l\'obbligo di verifica di cui all\'art. 3 né l\'acquisizione della dichiarazione prevista dall\'art. 6, e quindi la questione dell\'impossibilità di rapportarsi con il compratore non si pone.',
      'Gli adempimenti introdotti dal Decreto restano circoscritti alle fattispecie ivi espressamente contemplate.',
    ],
  },
  {
    id: 'sns-errore-3000',
    titolo: 'Il sistema restituisce «Errore 3000 – Veicolo con ostatività – Operazione non consentita» su un veicolo già sottoposto all\'intervento. Come si procede?',
    corpo: [
      'La casistica rientra tra i fermi verificatisi il 3 agosto e oggetto della prot. n. 386/SNS/ar del 4/8. A seguito della rimozione del blocco la pratica può essere ripresentata.',
      'Qualora l\'ostatività dovesse permanere anche dopo la ripresentazione, invitiamo ad aprire un ticket, conservando agli atti il Certificato di Verifica Takata e l\'evidenza acquisita presso l\'officina autorizzata che attesta l\'avvenuta esecuzione dell\'intervento, e a darcene comunicazione. Come chiarito al punto 2 della prot. n. 380/SNS/ar del 3/8, in presenza di richiamo già eseguito, e attestato dal sistema ufficiale del costruttore, la dichiarazione di cui all\'art. 6 non è dovuta.',
    ],
  },
  {
    id: 'sns-come-segnalare',
    titolo: 'Come si segnalano le anomalie riscontrate?',
    corpo: [
      'Attraverso l\'apposita funzione presente nell\'applicativo, accessibile dalla propria area riservata su https://studi.unasca.it/.',
    ],
  },
];
